"use client";

import { OfferRequest, OfferResponse, ApplicationRequest, Municipality, BrokerResponse, DocumentRequest, DocumentResponse } from "@/types/simpego-api";

declare global {
  interface Window {
    __env__: {
      apiUrl: string;
      widgetBasePath: string;
      defaultAgentId?: string;
      sentryEnv?: string;
      sentryDsn?: string;
      sentryRelease: string;
    };
  }
}

const API_BASE_URL = window.__env__.apiUrl;
const PREMIUM_CALCULATION_ENDPOINT = "/premium-calculations";
const OFFERS_ENDPOINT = "/offers";
const APPLICATIONS_ENDPOINT = "/applications";
const MUNICIPALITIES_ENDPOINT = "https://www.simpego.ch/.rest/v1/premiumareas/municipalities";
const BROKER_ENDPOINT = '/agents';
const BREEDS_ENDPOINT = '/breeds'; // New breeds endpoint

export const calculatePremium = async (payload: OfferRequest): Promise<OfferResponse> => {
  const response = await fetch(`${API_BASE_URL}${PREMIUM_CALCULATION_ENDPOINT}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorData = await response.json();
    console.error("Simpego API error (premium calculation):", errorData);
    throw new Error(errorData.message || "Failed to calculate premium from Simpego API.");
  }
  return response.json();
};

export const createOffer = async (payload: OfferRequest): Promise<OfferResponse> => {
  const response = await fetch(`${API_BASE_URL}${OFFERS_ENDPOINT}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorData = await response.json();
    console.error("Simpego API error (create offer):", errorData);
    throw new Error(errorData.message || "Failed to create offer from Simpego API.");
  }
  return response.json();
};

export const getOffer = async (contractToken: string, contractId: string): Promise<OfferResponse> => {
  const response = await fetch(`${API_BASE_URL}${OFFERS_ENDPOINT}/${contractToken}/${contractId}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const errorData = await response.json();
    console.error("Simpego API error (get offer):", errorData);
    throw new Error(errorData.message || "Failed to retrieve offer from Simpego API.");
  }
  return response.json();
};

export const createApplication = async (payload: ApplicationRequest): Promise<OfferResponse> => {
  const response = await fetch(`${API_BASE_URL}${APPLICATIONS_ENDPOINT}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorData = await response.json();
    console.error("Simpego API error (create application):", errorData);
    throw new Error(errorData.message || "Failed to create application from Simpego API.");
  }
  return response.json();
};

export const fetchMunicipalitiesByZip = async (zip: string): Promise<Municipality[]> => {
  const response = await fetch(`${MUNICIPALITIES_ENDPOINT}?zip=${zip}`);
  if (!response.ok) {
    const errorData = await response.json();
    console.error("Simpego API error (fetch municipalities):", errorData);
    throw new Error(errorData.message || "Failed to fetch municipalities.");
  }

  if (response.status === 204) {
    return [];
  }
  return response.json();
};

export const fetchBrokerByToken = async (token: string): Promise<BrokerResponse> => {
  const response = await fetch(`${API_BASE_URL}${BROKER_ENDPOINT}/${token}`);

  if (!response.ok) {
    const errorData = await response.json();
    console.error("Broker API error (get-broker):", errorData);
    throw new Error(errorData.message || "Failed to fetch broker by token.");
  }
  return response.json();
};

export const uploadDocument = async (
  contractToken: string,
  contractId: string,
  payload: DocumentRequest
): Promise<DocumentResponse> => {
  const response = await fetch(`${API_BASE_URL}${OFFERS_ENDPOINT}/${contractToken}/${contractId}/documents`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorData = await response.json();
    console.error("Simpego API error (upload document):", errorData);
    throw new Error(errorData.message || "Failed to upload document.");
  }
  return response.json();
};

export const deleteDocument = async (
  contractToken: string,
  contractId: string,
  documentId: number
): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}${OFFERS_ENDPOINT}/${contractToken}/${contractId}/documents/${documentId}`,
    {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    }
  );
  if (!response.ok) {
    const errorData = await response.json();
    console.error("Simpego API error (delete document):", errorData);
    throw new Error(errorData.message || "Failed to delete document.");
  }
};

// New function to fetch breeds
export const fetchBreeds = async (species: "cat" | "dog", locale: string): Promise<{ code: string; translation: string }[]> => {
  const response = await fetch(`${API_BASE_URL}${BREEDS_ENDPOINT}?species=${species}&language=${locale.toUpperCase()}`);
  if (!response.ok) {
    const errorData = await response.json();
    console.error("Simpego API error (fetch breeds):", errorData);
    throw new Error(errorData.message || "Failed to fetch breeds from Simpego API.");
  }
  const data = await response.json();
  return data.breeds || [];
};
